import React, { useMemo, useState, memo } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_Row,
  createMRTColumnHelper,
  type MRT_ColumnDef,
} from 'material-react-table';
import { Box, Button, Typography, Paper, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar } from '@mui/material';
import LoadingSpinner from '../shared/LoadingSpinner';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import * as QRCode from 'qrcode';
import { ColumnValuesFilter, columnValuesFilterFn } from './ColumnValuesFilter';

// Custom CSV export functions with improved error handling and fallbacks
const convertToCSV = (data: any[]): string => {
  if (!data || data.length === 0) return '';
  
  const headers = Object.keys(data[0]);
  const csvHeaders = headers.map(header => `"${header}"`).join(',');
  
  const csvRows = data.map(row => {
    return headers.map(header => {
      const value = row[header];
      if (value === null || value === undefined) return '""';
      if (typeof value === 'object') {
        return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
      }
      return `"${String(value).replace(/"/g, '""')}"`;
    }).join(',');
  });
  
  return [csvHeaders, ...csvRows].join('\n');
};

const downloadCSV = (data: any[], filename: string = 'table-export', onSuccess?: () => void, onError?: (error: string) => void) => {
  try {
    console.log('[TableViewer] Starting CSV export for:', filename);
    
    const csvContent = convertToCSV(data);
    if (!csvContent) {
      const error = 'No data available for export';
      console.warn('[TableViewer]', error);
      onError?.(error);
      return;
    }

    const timestamp = Date.now();
    const fullFilename = `${filename}-${timestamp}.csv`;
    
    // Prepend UTF-8 BOM so Excel detects the encoding correctly (otherwise it
    // assumes Windows-1252/ANSI and mangles non-Latin text like Arabic)
    const csvWithBom = '﻿' + csvContent;

    // Method 1: Try standard Blob + download attribute (works in most modern browsers)
    try {
      const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', fullFilename);
      link.style.visibility = 'hidden';
      
      // Append to body to ensure click works in all browsers
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
      
      console.log('[TableViewer] CSV exported successfully:', fullFilename);
      onSuccess?.();
      return;
    } catch (blobError) {
      console.warn('[TableViewer] Blob method failed, trying fallback:', blobError);
    }
    
    // Method 2: Fallback for environments with blob restrictions - use data URL
    try {
      const dataUrl = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvWithBom);
      const link = document.createElement('a');
      link.setAttribute('href', dataUrl);
      link.setAttribute('download', fullFilename);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('[TableViewer] CSV exported via data URL:', fullFilename);
      onSuccess?.();
      return;
    } catch (dataUrlError) {
      console.warn('[TableViewer] Data URL method failed:', dataUrlError);
    }
    
    // Method 3: Last resort - Open in new window for user to save
    try {
      const win = window.open('', '_blank');
      if (win) {
        win.document.write('<pre>' + csvContent.replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</pre>');
        win.document.title = fullFilename;
        console.log('[TableViewer] CSV opened in new window for manual save');
        onSuccess?.();
        return;
      }
    } catch (windowError) {
      console.warn('[TableViewer] Window method failed:', windowError);
    }
    
    // All methods failed
    const error = 'Unable to export CSV. Please try copying the data manually.';
    console.error('[TableViewer] All export methods failed');
    onError?.(error);
    
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    console.error('[TableViewer] Export error:', errorMsg);
    onError?.(errorMsg);
  }
};

interface TableViewerProps {
  data: any;
  endpoint?: string;
  reportTitle?: string;
  timestamp?: string;
  showCloseButton?: boolean;
  loading?: boolean;
  onOpenInNewWindow?: () => void;
}

interface TableRow {
  [key: string]: any;
}

const TableViewerSafe: React.FC<TableViewerProps> = ({ data, endpoint, reportTitle, timestamp, showCloseButton = true, loading = false, onOpenInNewWindow }) => {
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrValue, setQrValue] = useState<string>('');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  
  // Show loading state if requested
  if (loading) {
    return <LoadingSpinner />;
  }
  // Extract array data from various data structures
  const extractArrayData = (inputData: any): TableRow[] => {
    if (!inputData) return [];

    // FIRST: Check if data is already an array
    if (Array.isArray(inputData)) {
      // Check if this is an array of objects with 'docs' property
      if (inputData.length > 0 && inputData[0] && typeof inputData[0] === 'object' && inputData[0].docs && Array.isArray(inputData[0].docs)) {
        // Flatten all docs arrays into a single array of users
        const allUsers: any[] = [];
        inputData.forEach(item => {
          if (item.docs && Array.isArray(item.docs)) {
            allUsers.push(...item.docs);
          }
        });
        
        return allUsers;
      }
      
      // If it's a simple array of user objects, return directly
      return inputData;
    }
    
    // SECOND: Check if data is an object that contains an array
    if (typeof inputData === 'object' && inputData !== null) {
      // Check specifically for 'docs' first (common pagination pattern)
      if (inputData.docs && Array.isArray(inputData.docs)) {
        return inputData.docs;
      }
      
      // Check for other common data array properties
      const dataKeys = ['data', 'items', 'results', 'records', 'list'];
      
      for (const key of dataKeys) {
        if (inputData[key] && Array.isArray(inputData[key])) {
          return inputData[key];
        }
      }
      
      // If no common array property found, check all properties (excluding pagination meta)
      const arrayProperties = Object.entries(inputData)
        .filter(([key, value]) => Array.isArray(value) && key !== 'paginationmeta' && key !== 'paginationMeta')
        .filter(([_, value]) => (value as any[]).length > 0);
        
      if (arrayProperties.length === 1) {
        const [key, arrayValue] = arrayProperties[0];
        return arrayValue as any[];
      }
      
      // If no arrays found, return the object as a single-item array
      return [inputData];
    }
    
    // Handle primitive values
    return [{ value: inputData }];
  };

  // Helper functions defined before use
  const getColumnSize = (key: string): number => {
    switch (key) {
      case 'id':
      case 'userId':
      case 'User ID':
      case 'Referred ID':
      case 'Referring ID':
        return 100;
      case 'firstName':
      case 'lastName':
      case 'First Name':
      case 'Last Name':
      case 'Referred Name':
      case 'Referring Name':
        return 120;
      case 'phone':
      case 'Phone':
      case 'Referred Phone':
        return 140;
      case 'shopName':
      case 'company':
        return 200;
      case 'governorateData':
      case 'districtData':
      case 'Governorate':
      case 'District':
      case 'Referred District':
      case 'Referred Governorate':
        return 150;
      case 'Transaction Reference':
      case 'Referral Reward Value':
      case 'Disable Incentive Type':
        case 'Aggregation Date Time':
        return 250;
      case 'Distributor OUT Name':
      case 'Distributor OUT Date':
      case 'Electrician Use Name':
      case 'Electrician Use Date':
      case 'Total Redeemed Value':
      case 'Suspended By User ID':
      case 'Reactivated By User ID':
      case 'Gift Milestone Count':
      case 'Stamped For User Name':
      case 'Stamped For User Type':
      case 'Scanned By User Name':
      case 'Scanned By User Type':
      case 'Scanned By Scan Action':
        return 300;
      case 'QR Code':
      case 'Order Code':
      case 'Number of Months Since Registration':
      case 'Total Manual Redeemed Value':
      case 'Total paymop Redeemed Value':
        return 450; // Extra width for URLs with copy button
      case 'User Type':
      case 'Referred User Type':
        return 140;
      case 'Referred Date':
      case 'Referred Time':
      case 'Achieved Date':
      case 'Refer Date':
        return 140;
      case 'Referral Mechanism':
      case 'Referral Model Type':
      case 'Transaction ID':
        return 160;
      case 'Referral Incentive':
      case 'Incentive Status':
        return 130;
      case 'Min Consumption':
      case 'Percentage Per Scan':
      case 'Referral Period':
      case 'Total Points':
      case 'Total Money':
      case 'Value':
        return 140;
      // User Details Per Product Per Action columns
      case 'User Type Name':
      case 'Product Name':
      case 'Product Code':
      case 'Shop Name':
      case 'Wallet Type':
      case 'Scan Action':
      case 'User Name':
      case 'Product Type Name':
        return 150;
      case 'Counter':
      case 'Purchase Cash':
      case 'Sells Cash':
      case 'Use Cash':
      case 'Purchase Points':
      case 'Sells Points':
      case 'Use Points':
      case 'Backward Points':
      case 'Backward Money':
      case 'Wheel Points':
        return 120;
      case 'Created At':
        return 180;
      default:
        return 120;
    }
  };

  const formatCellValue = (value: any, key?: string): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>;
    }
    
    if (typeof value === 'object') {
      // Handle MongoDB ObjectId format
      if (value.$oid) {
        return <span style={{ fontFamily: 'monospace' }}>{value.$oid.substring(0, 8)}...</span>;
      }
      
      // Handle date objects
      if (value.$date) {
        const date = new Date(value.$date);
        return <span>{date.toLocaleDateString()}</span>;
      }
      
      // Handle coordinates arrays
      if (Array.isArray(value)) {
        if (value.length === 0) {
          return <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>;
        }
        if (value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number') {
          return <span>{value[0]?.toFixed(4)}, {value[1]?.toFixed(4)}</span>;
        }
        if (value.length <= 3) {
          return <span>{value.join(', ')}</span>;
        }
        return <span style={{ color: '#666' }}>Array ({value.length})</span>;
      }
      
      // Handle objects - find meaningful display value, skip id/_id fields
      const entries = Object.entries(value);
      if (entries.length === 0) {
        return <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>;
      }

      // Priority order for meaningful fields (skip id and _id)
      const meaningfulFields = [
        'name', 'title', 'label', 'description', 'text', 'value',
        'firstName', 'lastName', 'fullName', 'displayName',
        'email', 'phone', 'username', 'code', 'symbol',
        'street', 'city', 'address', 'location'
      ];

      // Filter out id and _id fields
      const nonIdEntries = entries.filter(([key]) => 
        key !== 'id' && key !== '_id' && key !== '$oid'
      );

      if (nonIdEntries.length === 0) {
        // If only id fields exist, show the first meaningful one
        const idEntry = entries.find(([key]) => key === 'id' || key === '_id');
        return idEntry ? <span style={{ fontFamily: 'monospace', color: '#666' }}>{String(idEntry[1]).substring(0, 8)}...</span> : <span>-</span>;
      }

      // Try to find a meaningful field in priority order
      for (const field of meaningfulFields) {
        const found = nonIdEntries.find(([key]) => key.toLowerCase().includes(field.toLowerCase()));
        if (found && found[1] && String(found[1]).trim()) {
          return <span>{String(found[1])}</span>;
        }
      }

      // If no meaningful field found, show first non-id field
      const firstMeaningful = nonIdEntries[0];
      if (firstMeaningful && firstMeaningful[1]) {
        return <span>{String(firstMeaningful[1])}</span>;
      }

      // If still nothing meaningful, show a summary
      return <span style={{ color: '#666' }}>Object ({entries.length} props)</span>;
    }
    
    if (typeof value === 'boolean'&& key==='enabled') {
      return (
        <span style={{ 
          color: value ? '#4caf50' : '#f44336',
          fontWeight: 'bold'
        }}>
          {value ? 'Activated' : 'Suspended'}
        </span>
      );
    }

     if (typeof value === 'boolean') {
      return (
        <span style={{ 
          color: value ? '#4caf50' : '#f44336',
          fontWeight: 'bold'
        }}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }
    
    if (typeof value === 'number') {
      // Check if this is an ID field - don't add commas to IDs
      const isIdField = key && (
        key.toLowerCase().includes('id') ||
        key.toLowerCase().includes('user id') ||
        key.toLowerCase().includes('energya id') ||
        key.toLowerCase().includes('referred id') ||
        key.toLowerCase().includes('referring id') ||
        key.toLowerCase().includes('transaction id') ||
        key.toLowerCase().includes('warehouse id') ||
        key.toLowerCase().includes('user type id')
      );
      
      return <span>{isIdField ? value.toString() : value.toLocaleString()}</span>;
    }
    
    if (typeof value === 'string') {
      // Handle URLs specifically
      if (value.startsWith('http://') || value.startsWith('https://') || /^(www\.)?q7q\.me/i.test(value)) {
        const hideQrButton = key && ['shop image', 'image url'].includes(key.toLowerCase());
        const linkUrl = /^(www\.)?q7q\.me/i.test(value) && !value.startsWith('http')
          ? `https://${value}`
          : value;

        const handleCopyUrl = async (url: string, event: React.MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          try {
            await navigator.clipboard.writeText(url);
            // Show a temporary success indicator
            const button = event.currentTarget as HTMLElement;
            const originalText = button.textContent;
            button.textContent = 'Copied!';
            button.style.color = '#4caf50';
            setTimeout(() => {
              button.textContent = originalText;
              button.style.color = '#1976d2';
            }, 1500);
          } catch (err) {
            console.error('Failed to copy URL:', err);
          }
        };

        const handleShowQR = async (url: string, event: React.MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          try {
            setQrValue(url);
            const qrDataUrl = await QRCode.toDataURL(url, {
              errorCorrectionLevel: 'H' as const,
              type: 'image/png' as const,
              margin: 1,
              width: 300,
            });
            setQrDataUrl(qrDataUrl);
            setQrModalOpen(true);
          } catch (err) {
            console.error('Failed to generate QR code:', err);
          }
        };

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%', minWidth: '300px' }}>
            <span 
              title={value}
              style={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                flex: 1,
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                color: '#1976d2',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(linkUrl, '_blank');
              }}
            >
              {value.length > 80 ? `${value.substring(0, 77)}...` : value}
            </span>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => handleCopyUrl(linkUrl, e)}
              sx={{
                minWidth: 'auto',
                px: 1,
                py: 0.5,
                fontSize: '0.75rem',
                textTransform: 'none',
                flexShrink: 0
              }}
            >
              Copy
            </Button>
            {!hideQrButton && (
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => handleShowQR(linkUrl, e)}
                sx={{
                  minWidth: 'auto',
                  px: 1,
                  py: 0.5,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  flexShrink: 0,
                  color: '#ff9800',
                  borderColor: '#ff9800',
                  '&:hover': {
                    backgroundColor: '#fff3e0',
                    borderColor: '#fb8c00'
                  }
                }}
                startIcon={<QrCode2Icon sx={{ fontSize: '1rem !important' }} />}
              >
                QR
              </Button>
            )}
          </Box>
        );
      }
      
      // Handle long non-URL strings
      if (value.length > 50) {
        return <span title={value}>{value.substring(0, 47)}...</span>;
      }
      
      // Check if it's a date string
      const dateCheck = new Date(value);
      if (!isNaN(dateCheck.getTime()) && (value.includes('-') || value.includes('T'))) {
        return <span>{dateCheck.toLocaleDateString()} {dateCheck.toLocaleTimeString()}</span>;
      }
      
      return <span>{value}</span>;
    }
    
    return <span>{String(value)}</span>;
  };

  // Memoize the table data
  const tableData = useMemo(() => {
    if (!data) return [];
    return extractArrayData(data);
  }, [data]);

  // Generate columns dynamically based on the data
  const columns = useMemo<MRT_ColumnDef<TableRow>[]>(() => {
    if (!tableData || tableData.length === 0) return [];

    // Get all unique keys from all objects in the array
    const allKeys = new Set<string>();
    tableData.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        Object.keys(item).forEach(key => allKeys.add(key));
      }
    });

    // For formatted data (like Referral Users, Referred Users, User Details Per Product Per Action, Deactivated Incentive QR, Order Scanout From Warehouse, Warehouse Quantity Aggregation, Wallet Transactions, Return System, Scan Per Product details), preserve the original order
    const isFormattedData = tableData && tableData.length > 0 && 
      (tableData[0]['Transaction Reference'] || tableData[0]['Transaction ID'] || 
       tableData[0]['User ID'] || tableData[0]['Energya ID'] || 
       tableData[0]['Referring ID'] || tableData[0]['Referred ID'] ||
       tableData[0]['User Type Name'] || tableData[0]['Product Name'] ||
       tableData[0]['QR Code'] || tableData[0]['ID'] ||
       tableData[0]['Value'] || tableData[0]['Warehouse Details'] ||
       tableData[0]['Warehouse ID'] || tableData[0]['SKU Name'] ||
       tableData[0]['Index'] || tableData[0]['Transaction Type'] ||
       tableData[0]['Balance Before'] || tableData[0]['Full Name'] ||
       tableData[0]['Warehouse'] || tableData[0]['Warehouse User ID'] ||
       tableData[0]['Products Name'] || tableData[0]['Products Code'] ||
       tableData[0]['Distributor In'] || tableData[0]['Retailer In'] ||
       tableData[0]['Electrician USE'] || tableData[0]['Quantity'] ||
       tableData[0]['Purchase Points'] || tableData[0]['Sells Points'] ||
       tableData[0]['Use Points'] || tableData[0]['Action'] ||
       tableData[0]['QR Value'] || tableData[0]['QR Type'] ||
       tableData[0]['Scan Action'] || tableData[0]['Backword Points'] ||
       tableData[0]['Wheel Points'] || tableData[0]['Gift Name'] ||
       tableData[0]['Gift Value'] || tableData[0]['Wheel Name'] ||
       tableData[0]['Milestone Name']);
    
    let headers: string[];
    
    if (isFormattedData) {
      // Preserve the exact order from the first object for formatted data
      headers = Object.keys(tableData[0]);
    } else {
      // Use the original filtering and sorting logic for raw data
      headers = Array.from(allKeys)
        .filter(key => {
          // Skip internal MongoDB fields
          if (key.startsWith('__') || key === '_id') return false;
          
          // Check if the value is a complex object
          const sampleValue = tableData[0][key];
          if (typeof sampleValue === 'object' && sampleValue !== null && !Array.isArray(sampleValue)) {
            // Always show MongoDB ObjectId and dates in main table
            if (sampleValue.$oid || sampleValue.$date) return true;
            
            // For other objects, check if they have meaningful content (not just id fields)
            const entries = Object.entries(sampleValue);
            const nonIdEntries = entries.filter(([k]) => k !== 'id' && k !== '_id' && k !== '$oid');
            
            // Show objects that have meaningful fields or are simple (1-3 total fields)
            if (nonIdEntries.length > 0 && entries.length <= 3) return true;
            
            // Hide complex objects with many fields
            return false;
          }
          return true;
        })
        .sort((a, b) => {
          // Prioritize important fields
          const priority = [
            'userId', 'firstName', 'lastName', 'phone', 'shopName', 
            'governorateData', 'districtData', 'userType', 'segment',
            'createdAt', 'parentProfile', 'coordinates', 'enabled', 
            'acceptedById', 'acceptedBy', 'firebasePromoterName', 
            'isDeleted', 'userHoldComment'
          ];
          const aIndex = priority.indexOf(a);
          const bIndex = priority.indexOf(b);
          
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return a.localeCompare(b);
        });
    }

    // Create columns using Material React Table column helper
    const columnHelper = createMRTColumnHelper<TableRow>();
    
    return headers.map(key =>
      columnHelper.accessor(key, {
        // For formatted data, use the key as is; for raw data, capitalize first letter
        header: isFormattedData ? key : (key.charAt(0).toUpperCase() + key.slice(1)),
        size: getColumnSize(key),
        Cell: ({ cell }) => formatCellValue(cell.getValue(), key),
        // Google Sheets-style "filter by values" - checkbox list of unique values instead of
        // a plain text search. Registered by name below (filterFns.columnValues) since MRT
        // overwrites inline filterFn functions while preparing columns.
        filterFn: 'columnValues' as any,
        Filter: ({ column, table }) => (
          <ColumnValuesFilter column={column} table={table} columnKey={key} />
        ),
      })
    );
  }, [tableData, getColumnSize, formatCellValue]);

  // Export functions
  const handleExportRows = (rows: MRT_Row<TableRow>[]) => {
    const rowData = rows.map((row) => row.original);
    const filename = endpoint?.replace(/\//g, '_') || 'selected-rows';
    downloadCSV(rowData, filename,
      () => {
        setSnackbarMessage(`✓ Successfully exported ${rowData.length} row(s) to ${filename}.csv`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      },
      (error) => {
        setSnackbarMessage(`✗ Export failed: ${error}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    );
  };

  const handleExportData = () => {
    const filename = endpoint?.replace(/\//g, '_') || 'all-data';
    downloadCSV(tableData, filename,
      () => {
        setSnackbarMessage(`✓ Successfully exported ${tableData.length} record(s) to ${filename}.csv`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      },
      (error) => {
        setSnackbarMessage(`✗ Export failed: ${error}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    );
  };

  // Error boundary for Material React Table
  
  // Remove custom debouncing - let Material React Table handle it natively
  
  // Move the useMaterialReactTable hook outside of useMemo to fix React Hooks violation
  const table = useMaterialReactTable({
        columns,
        data: tableData,
        enableRowSelection: true,
        enableColumnFilters: true,
        enableGlobalFilter: true,
        enableSorting: true,
        enablePagination: true,
        columnFilterDisplayMode: 'popover',
        paginationDisplayMode: 'pages',
        positionToolbarAlertBanner: 'bottom',
        globalFilterFn: 'contains',
        enableGlobalFilterModes: true,
        // Register the custom "filter by values" filter fn used by every column (see Filter/filterFn above)
        filterFns: { columnValues: columnValuesFilterFn },
        // Default tooltip interpolates the filter fn name + raw value (e.g. `by undefined "a" or "b"`);
        // our filter value is a plain values array, so drop the placeholders instead of showing that.
        localization: { filteringByColumn: 'Filtering by {column}' } as any,
        enableRowVirtualization: false, // Disable to improve scrolling performance
        enableColumnVirtualization: false, // Disable for better performance  
        // enableColumnResizing: true, // Enable for user to resize columns
        enableColumnOrdering: true, // Enable drag and drop column reordering
        enableHiding: true, // Disable column hiding for performance
        enableFullScreenToggle: showCloseButton, // Enable full screen when opened in new window
        enableStickyHeader: true, // Enable sticky header - FIXED
        // Optimize search input behavior
        muiSearchTextFieldProps: {
          placeholder: 'Search all columns...',
          sx: {
            minWidth: '250px',
            '& input': {
              fontSize: '0.875rem',
            }
          },
          variant: 'outlined',
          size: 'small'
        },
        initialState: {
          density: 'compact',
          pagination: { pageIndex: 0, pageSize: 10 } // Increase page size for fewer renders
        },
        // Add more pagination options
        muiPaginationProps: {
          rowsPerPageOptions: [10,25, 50, 100, 200, 500],
          showFirstButton: true,
          showLastButton: true,
        },
        muiTableContainerProps: {
          sx: {
            flex: 1,
            minHeight: 0,
            overflowY: 'visible',
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            willChange: 'scroll-position',
            transform: 'translateZ(0)',
          }
        },
        muiTablePaperProps: {
          sx: {
            boxShadow: 'none',
            backgroundColor: '#fafafa',
            overflow: 'visible',
            display: 'block',
            flexDirection: 'column',
            height: 'auto'
          }
        },
        muiTableHeadCellProps: {
          sx: {
            fontWeight: '600',
            fontSize: '13px',
            padding: '10px 12px',
            backgroundColor: '#f9fafb',
            color: '#4b5563',
            borderBottom: '1px solid #e5e7eb',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            '&:hover': {
              backgroundColor: '#f3f4f6',
              cursor: 'pointer'
            },
          }
        },
        // Style filter buttons to look like Google Sheets
        muiFilterCheckboxProps: {
          sx: {
            color: '#3f51b5 !important',
            '&.Mui-checked': {
              color: '#3f51b5 !important',
            }
          }
        },
        // Configure the filter popover
        muiFilterTextFieldProps: {
          placeholder: 'Filter...',
          variant: 'outlined',
          size: 'small',
          sx: {
            m: 1,
            minWidth: 200,
          }
        },
        muiTableBodyCellProps: {
          sx: {
            fontSize: '13px',
            padding: '10px 12px',
            borderBottom: '1px solid #e5e7eb',
            color: '#111827',
          },
        },
        muiTableBodyRowProps: {
          sx: {
            backgroundColor: '#ffffff',
            '&:hover': {
              backgroundColor: '#f3f4f6 !important',
            },
            // Optimize for scrolling performance
            contain: 'layout style',
            willChange: 'auto',
          },
        },
        muiTopToolbarProps: {
          sx: {
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef',
            padding: '8px'
          }
        },
        muiBottomToolbarProps: {
          sx: {
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
            minHeight: '56px', // Ensure minimum height for pagination
            display: 'flex !important', // Force display
            visibility: 'visible !important', // Force visibility
            position: 'relative', // Ensure proper positioning
            zIndex: 10 // Ensure it's above other elements
          }
        },
        renderTopToolbarCustomActions: ({ table }) => (
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              padding: '4px',
              flexWrap: 'wrap',
            }}
          >
            <Button
              onClick={handleExportData}
              startIcon={<FileDownloadIcon />}
              variant="contained"
              size="small"
              sx={{ 
                backgroundColor: '#000000',
                '&:hover': { backgroundColor: '#333333' },
                fontSize: '0.7rem',
                padding: '4px 8px'
              }}
            >
              EXPORT ALL DATA
            </Button>
            
            <Button
              disabled={table.getRowModel().rows.length === 0}
              onClick={() => handleExportRows(table.getRowModel().rows)}
              startIcon={<FileDownloadIcon />}
              variant="contained"
              size="small"
              sx={{ 
                backgroundColor: '#000000',
                '&:hover': { backgroundColor: '#333333' },
                fontSize: '0.7rem',
                padding: '4px 8px'
              }}
            >
              EXPORT PAGE ROWS
            </Button>
            <Button
              disabled={!table.getState().globalFilter && table.getState().columnFilters.length === 0}
              onClick={() =>
                handleExportRows(table.getPrePaginationRowModel().rows)
              }
              startIcon={<FileDownloadIcon />}
              variant="outlined"
              size="small"
              sx={{ 
                fontSize: '0.7rem',
                padding: '4px 8px',
                borderColor: '#000000',
                color: '#000000',
                '&:hover': { 
                  borderColor: '#333333',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              EXPORT FILTERED ROWS
            </Button>
            <Button
              disabled={
                !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
              }
              onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
              startIcon={<FileDownloadIcon />}
              variant="outlined"
              size="small"
              sx={{ 
                fontSize: '0.7rem',
                padding: '4px 8px',
                borderColor: '#000000',
                color: '#000000',
                '&:hover': { 
                  borderColor: '#333333',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              EXPORT SELECTED ROWS
            </Button>
            
            {onOpenInNewWindow && (
              <Button
                onClick={onOpenInNewWindow}
                startIcon={<OpenInNewIcon />}
                variant="contained"
                size="small"
                sx={{ 
                  backgroundColor: '#000000',
                  '&:hover': { backgroundColor: '#333333' },
                  fontSize: '0.7rem',
                  padding: '4px 8px'
                }}
              >
                OPEN IN NEW WINDOW
              </Button>
            )}
            <Button
              onClick={() => window.print()}
              startIcon={<PrintIcon />}
              variant="outlined"
              size="small"
              sx={{ 
                fontSize: '0.7rem',
                padding: '4px 8px',
                borderColor: '#607d8b',
                color: '#607d8b',
                '&:hover': { 
                  borderColor: '#455a64',
                  backgroundColor: '#eceff1'
                }
              }}
            >
              PRINT
            </Button>
            {showCloseButton && (
              <Button
                onClick={() => window.close()}
                startIcon={<CloseIcon />}
                variant="contained"
                size="small"
                sx={{ 
                  backgroundColor: '#f44336',
                  '&:hover': { backgroundColor: '#d32f2f' },
                  fontSize: '0.7rem',
                  padding: '4px 8px'
                }}
              >
                CLOSE
              </Button>
            )}
          </Box>
        ),
      });

  if (!table) {
    return (
      <Box sx={{ padding: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          There was an error initializing the advanced table. Falling back to basic view.
        </Alert>
        <Paper sx={{ padding: 2 }}>
          <Typography variant="h6" gutterBottom>
            Data Preview (Basic Mode)
          </Typography>
          <pre style={{ overflow: 'auto', maxHeight: '400px' }}>
            {JSON.stringify(tableData ? tableData.slice(0, 10) : [], null, 2)}
          </pre>
          {tableData && tableData.length > 10 && (
            <Typography variant="body2" color="text.secondary">
              Showing first 10 items out of {tableData.length} total items.
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <Button
              onClick={handleExportData}
              startIcon={<FileDownloadIcon />}
              variant="contained"
            >
              Export as CSV
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 1 }} className="table-viewer">
      {/* Compact Header */}
      <Paper sx={{ 
        padding: 1.5, 
        marginBottom: 1, 
        background: '#000000',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ 
            backgroundColor: 'rgba(255,255,255,0.2)', 
            borderRadius: '4px', 
            padding: '4px 8px',
            fontSize: '0.75rem',
            fontWeight: 'bold'
          }}>
            📊 Table Viewer
          </Box>
          {(reportTitle || endpoint) && (
            <Box sx={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              borderRadius: '12px',
              padding: '2px 8px',
              fontSize: '0.7rem'
            }}>
              {reportTitle || endpoint}
            </Box>
          )}
          <Box sx={{ 
            backgroundColor: 'rgba(255,255,255,0.15)', 
            borderRadius: '12px', 
            padding: '2px 8px',
            fontSize: '0.7rem'
          }}>
            {tableData ? tableData.length : 0} Records
          </Box>
          {timestamp && (
            <Box sx={{ 
              fontSize: '0.65rem', 
              opacity: 0.8,
              marginLeft: 'auto'
            }}>
              Generated: {new Date(timestamp).toLocaleDateString()} {new Date(timestamp).toLocaleTimeString()}
            </Box>
          )}
        </Box>
      </Paper>
      
      <Paper sx={{ 
        borderRadius: '8px',
        overflow: 'visible',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        // height: showCloseButton ? '75vh' : '63vh', // Constrain paper height
        // display: 'flex',
        // flexDirection: 'column'
      }}>
        {tableData && tableData.length > 0 ? (
          <Box 
            className="table-viewer-content"
            sx={{ 
              '& .MuiPaper-root': {
                boxShadow: 'none'
              },
              overflow: 'visible',
              display: 'block'
            }}>
            {table && <MaterialReactTable table={table} />}
          </Box>
        ) : (
          <Box sx={{ padding: 4, textAlign: 'center', color: '#666' }}>
            <Typography variant="h6">No data available</Typography>
            <Typography variant="body2">Please select an endpoint and execute to view data</Typography>
          </Box>
        )}
      </Paper>

      {/* QR Code Display Modal */}
      <Dialog 
        open={qrModalOpen} 
        onClose={() => setQrModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          QR Code
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: 3 }}>
          {qrDataUrl && (
            <Box>
              <img 
                src={qrDataUrl} 
                alt="QR Code" 
                style={{ 
                  maxWidth: '100%',
                  border: '8px solid #f5f5f5',
                  borderRadius: '8px'
                }} 
              />
            </Box>
          )}
          <Typography variant="caption" color="textSecondary" sx={{ wordBreak: 'break-all', textAlign: 'center', maxHeight: '80px', overflow: 'auto' }}>
            {qrValue}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: 2, gap: 1 }}>
          <Button 
            onClick={() => setQrModalOpen(false)}
            variant="outlined"
          >
            Close
          </Button>
          <Button 
            onClick={() => {
              const link = document.createElement('a');
              link.href = qrDataUrl;
              link.download = `qr-code-${Date.now()}.png`;
              link.click();
            }}
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for export notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default memo(TableViewerSafe);